import React, { type PropsWithChildren } from 'react';
import AddItemIcon from '@atlaskit/icon/core/migration/shortcut--add-item';
import FileIcon from '@atlaskit/icon/glyph/file';
import PageIcon from '@atlaskit/icon/glyph/page';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { Shortcut as ShortcutItem } from '@atlassian/jira-navigation-apps-shortcuts-sidebar/src/ui/shortcut/main.tsx';
import { MenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { useShortcutsData } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/shortcuts/shortcuts-data/index.tsx';
import {
	ShortcutsContainer,
	useShortcutItems,
} from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/shortcuts/state-container/index.tsx';
import ShortcutsApp from '@atlassian/jira-navigation-shortcuts/src/index.tsx';
import {
	useIsModalOpenViaAddView,
	useSimplifyNavExperimentActions,
} from '@atlassian/jira-onboarding-simplify-navigation-experiment/src/common/controllers/add-shortcut/index.tsx';
import { useIsSimplifyNavExperimentEnabled } from '@atlassian/jira-onboarding-simplify-navigation-experiment/src/common/utils/is-simplify-nav-experiment-enabled/index.tsx';
import { isDevopsFeatureDisabledInFedRamp } from '@atlassian/jira-software-devops-fedramp-utils/src/index.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { messages } from './messages.tsx';
import { PagesMenuItem } from './pages-menu-item/index.tsx';
import { messages as pagesMenuItemMessages } from './pages-menu-item/messages.tsx';
import type { ShortcutsProps } from './types.tsx';

const ShortcutsSimplifyNavExperimentInner = ({ children }: PropsWithChildren<{}>) => {
	const isSimplifyNavExperimentEnabled = useIsSimplifyNavExperimentEnabled();

	return isSimplifyNavExperimentEnabled ? null : <>{children}</>;
};
const ShortcutsSimplifyNavExperiment = ({ children }: PropsWithChildren<{}>) => (
	<JSErrorBoundary
		packageName="jiraNavigationAppsSidebarCommon"
		id="sidebar.shortcuts"
		teamName="jlove-makkuro"
		fallback={() => <>{children}</>}
	>
		<ShortcutsSimplifyNavExperimentInner>{children}</ShortcutsSimplifyNavExperimentInner>
	</JSErrorBoundary>
);

export const ShortcutsInner = ({
	// @ts-expect-error - TS2322 - Type 'null' is not assignable to type 'string'.
	addItemLabel = null,
	canAddItem,
	canAddLinks,
	canAddRepositories,
	children = null,
	hasPages = false,
	isSimplifiedProject = false,
	pagesLink,
	projectId,
	onPagesTabClick,
	projectKey,
	isBusinessProject = false,
	canShowOtherShortcuts = true,
}: ShortcutsProps) => {
	const [{ data: rawShortcuts }, actions] = useShortcutItems();
	const baseUrl = '';
	const isAdmin = useIsAdmin();
	const { formatMessage } = useIntl();
	const isProjectPagesEnabled = hasPages && !isDevopsFeatureDisabledInFedRamp();
	const [isModalOpenViaAddView] = useIsModalOpenViaAddView();
	const { setIsModalOpenViaAddView } = useSimplifyNavExperimentActions();
	if (!rawShortcuts || !actions) return <>{children}</>;

	// We need this id to focus edit shortcut button after closing edit modal for repo type shortcut, which is legacy
	const EDIT_SHORTCUT_BUTTON_ID_PREFIX = 'navigation-apps-sidebar.shortcuts.edit-shortcut-button-';
	const shortcuts = rawShortcuts.map((s) => ({
		...s,
		editButtonId: EDIT_SHORTCUT_BUTTON_ID_PREFIX + s.id,
	}));

	const urlForSoftwareProjects = isSimplifiedProject
		? `/jira/software/projects/${projectKey}/pages`
		: `/jira/software/c/projects/${projectKey}/pages`;

	// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
	const Icon = isBusinessProject ? FileIcon : PageIcon;

	return (
		<ShortcutsApp
			// Shortcuts app from nav-next expect the navItemType to be NavigationItem, which is deprecated
			// @ts-expect-error - TS2322 - Type 'BoundActions<State, { readonly addItem: (item: ShortcutItem) => Action<State, void, void | Promise<void>>; readonly deleteItem: (id: string) => Action<State, void, void | Promise<...>>; readonly editItem: (item: ShortcutItem) => Action<...>; readonly editItems: (items: ShortcutItem[]) => Action<...>; readonly update...' is not assignable to type '{ addItem: (arg1: NavigationItem) => any; deleteItem: (arg1: string) => any; editItem: (arg1: NavigationItem) => any; editItems: (arg1: NavigationItem[]) => any; }'.
			methods={actions}
			// @ts-expect-error - TS2322 - Type 'ShortcutItem[]' is not assignable to type 'NavigationItem[]'.
			items={shortcuts}
			baseUrl={baseUrl}
			projectKey={projectKey}
			projectId={projectId}
			isAdmin={isAdmin}
			showPagesSelector={!isSimplifiedProject}
			canAddLinks={canAddLinks}
			canAddRepositories={canAddRepositories}
			pagesLink={pagesLink}
			{...(fg('jsw_simplify_navigation_m2_gate') && {
				isModalOpenViaAddView,
				setIsModalOpenViaAddView,
			})}
		>
			{({ openAddModal, openEditModal, openManagePagesLinkModal, addItemButtonRef }) => (
				<>
					{isProjectPagesEnabled && (
						<PagesMenuItem
							analytics={{
								itemId: 'pages',
								// @ts-expect-error TS2322 | TS2322 - Type '{ itemId: string; nextgen: boolean; projectId: number; connectionState: DevOpsToolConnectionState | undefined; }' is not assignable to type 'Analytics'. | Type '{ itemId: string; nextgen: boolean; projectId: number; connectionState: DevOpsToolConnectionState | undefined; }' is not assignable to type 'Analytics'.
								nextgen: isSimplifiedProject,
								projectId,
							}}
							aria-labelledby="navigation-apps-sidebar-common.ui.shortcuts.business-pages-link"
							// eslint-disable-next-line jira/integration/test-id-by-folder-structure, jira/integration/enforce-data-testid-usage
							data-test-id="navigation-apps-sidebar-common.ui.shortcuts.business-pages-link"
							href={
								isBusinessProject
									? `/jira/core/projects/${projectKey}/pages`
									: urlForSoftwareProjects
							}
							projectId={projectId}
							iconBefore={<Icon label="" />}
							onClick={onPagesTabClick}
							selectedOn={['project-pages']}
							onEditClick={pagesLink?.canEdit ? openManagePagesLinkModal : undefined}
							pagesMenuItemTitle={isBusinessProject ? pagesMenuItemMessages.pagesItem : undefined}
						/>
					)}
					{canShowOtherShortcuts &&
						shortcuts.map((shortcut) => (
							<ShortcutItem shortcut={shortcut} key={shortcut.id} openEditItem={openEditModal} />
						))}
					{children}
					{canAddItem &&
						(fg('jsw_simplify_navigation_m2_gate') ? (
							<ShortcutsSimplifyNavExperiment>
								<MenuItem
									analytics={{ itemId: 'shortcuts--add' }}
									iconBefore={<AddItemIcon spacing="spacious" label="" />}
									onClick={openAddModal}
									ref={addItemButtonRef}
									// eslint-disable-next-line jira/integration/test-id-by-folder-structure
									data-testid="navigation-apps-sidebar-common.ui.shortcuts.shortcuts-add-link"
								>
									{addItemLabel || formatMessage(messages.addItem)}
								</MenuItem>
							</ShortcutsSimplifyNavExperiment>
						) : (
							<MenuItem
								analytics={{ itemId: 'shortcuts--add' }}
								iconBefore={<AddItemIcon spacing="spacious" label="" />}
								onClick={openAddModal}
								ref={addItemButtonRef}
								// eslint-disable-next-line jira/integration/test-id-by-folder-structure
								data-testid="navigation-apps-sidebar-common.ui.shortcuts.shortcuts-add-link"
							>
								{addItemLabel || formatMessage(messages.addItem)}
							</MenuItem>
						))}
				</>
			)}
		</ShortcutsApp>
	);
};

export const Shortcuts = ({ children = null, ...rest }: ShortcutsProps) => {
	const { data, loading } = useShortcutsData();
	if (!data) return <>{children}</>;
	return (
		<ShortcutsContainer data={data} loading={loading}>
			<ShortcutsInner {...rest}>{children}</ShortcutsInner>
		</ShortcutsContainer>
	);
};
